/**
 * ============================================================================
 * ADMIN NOTIFICATIONS PAGE
 * ============================================================================
 * Same structure as Admin Finance: hero, stats bento, filters, list, pagination.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useAdminStore } from '../../store';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Spinner from '../../components/common/Spinner';
import Button from '../../components/common/Button';
import NotificationItem from '../../components/notification/NotificationItem';
import '../../styles/landing.css';
import '../../styles/animations.css';
import '../../styles/global.css';

/** Normalize backend notification (snake_case + nested user) for display */
function normalizeNotification(row) {
  if (!row) return null;
  const user = row.user || {};
  return {
    ...row,
    id: row.id,
    read: !!row.is_read,
    isRead: !!row.is_read,
    type: row.type || 'info',
    title: row.title || '',
    message: row.message || '',
    priority: row.priority || 'normal',
    createdAt: row.created_at ?? row.createdAt,
    created_at: row.created_at,
    transactionId: row.transaction_id ?? row.transactionId,
    disputeId: row.dispute_id ?? row.disputeId,
    userName: user.full_name ?? user.fullName ?? user.phone ?? '—',
  };
}

const AdminNotifications = () => {
  const navigate = useNavigate();
  const {
    listAllNotifications,
    markNotificationAsRead,
    deleteNotification,
    loading,
  } = useAdmin();
  const { pagination, setPagination } = useAdminStore();
  const { showError, showSuccess } = useToast();
  const { formatDate, language } = useI18n();

  const [notifications, setNotifications] = useState([]);
  const [notificationsPagination, setNotificationsPagination] = useState(null);
  const [statsSummary, setStatsSummary] = useState({ total: 0, unread: 0, urgent: 0, today: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const data = await listAllNotifications({
        page: pagination?.page ?? 1,
        limit: pagination?.pageSize ?? 20,
        type: typeFilter || undefined,
        priority: priorityFilter || undefined,
        status: statusFilter || undefined,
        search: appliedSearch.trim() || undefined,
      });
      const raw = data?.data ?? data;
      const list = Array.isArray(raw?.notifications) ? raw.notifications : (raw?.notifications ? [] : []);
      setNotifications(list);
      setNotificationsPagination(raw?.pagination ?? null);
      // Stats from current page for display; total from pagination
      const total = raw?.pagination?.total ?? list.length;
      const unread = list.filter((n) => !(n.is_read || n.read)).length;
      const urgent = list.filter((n) => n.priority === 'urgent').length;
      const today = list.filter((n) => {
        const d = new Date(n.created_at ?? n.createdAt);
        const t = new Date();
        return d.toDateString() === t.toDateString();
      }).length;
      setStatsSummary({ total, unread, urgent, today });
    } catch (error) {
      showError(
        error.message ||
          (language === 'fr' ? 'Échec du chargement des notifications' : 'Failed to load notifications')
      );
      console.error('Load notifications error:', error);
      setNotifications([]);
      setNotificationsPagination(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [
    pagination?.page,
    pagination?.pageSize,
    typeFilter,
    priorityFilter,
    statusFilter,
    appliedSearch,
  ]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setStatsInView(true));
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSearch = () => {
    setAppliedSearch(searchQuery);
    setPagination({ ...pagination, page: 1 });
  };
  const handleClear = () => {
    setSearchQuery('');
    setAppliedSearch('');
    setTypeFilter('');
    setPriorityFilter('');
    setStatusFilter('');
    setPagination({ ...pagination, page: 1 });
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      showSuccess(language === 'fr' ? 'Notification marquée comme lue' : 'Notification marked as read');
      await loadData();
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec' : 'Failed'));
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      showSuccess(language === 'fr' ? 'Notification supprimée' : 'Notification deleted');
      await loadData();
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec' : 'Failed'));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unread = notifications.filter((n) => !(n.is_read || n.read));
      await Promise.all(unread.map((n) => markNotificationAsRead(n.id)));
      showSuccess(
        language === 'fr' ? 'Toutes les notifications marquées comme lues' : 'All notifications marked as read'
      );
      await loadData();
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec' : 'Failed'));
    }
  };

  const typeOptions = [
    { value: '', label: language === 'fr' ? 'Tous les types' : 'All Types' },
    { value: 'payment', label: language === 'fr' ? 'Paiement' : 'Payment' },
    { value: 'delivery', label: language === 'fr' ? 'Livraison' : 'Delivery' },
    { value: 'dispute', label: language === 'fr' ? 'Litige' : 'Dispute' },
    { value: 'system', label: language === 'fr' ? 'Système' : 'System' },
    { value: 'transaction', label: language === 'fr' ? 'Transaction' : 'Transaction' },
  ];

  const priorityOptions = [
    { value: '', label: language === 'fr' ? 'Toutes les priorités' : 'All Priorities' },
    { value: 'urgent', label: language === 'fr' ? 'Urgent' : 'Urgent' },
    { value: 'high', label: language === 'fr' ? 'Haute' : 'High' },
    { value: 'normal', label: language === 'fr' ? 'Normale' : 'Normal' },
    { value: 'low', label: language === 'fr' ? 'Basse' : 'Low' },
  ];

  const statusOptions = [
    { value: '', label: language === 'fr' ? 'Tous' : 'All' },
    { value: 'unread', label: language === 'fr' ? 'Non lues' : 'Unread' },
    { value: 'read', label: language === 'fr' ? 'Lues' : 'Read' },
  ];

  const pag = notificationsPagination ?? { page: 1, limit: 20, total: 0, totalPages: 0 };
  const totalCount = pag.total ?? notifications.length;
  const showPagination = totalCount > (pagination?.pageSize ?? 20);

  if (isLoading && notifications.length === 0) {
    return (
      <AdminLayout hideFooter>
        <div className="transactions-wt transactions-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement des notifications...' : 'Loading notifications...'}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />

          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              🔔 {language === 'fr' ? 'Gestion des notifications' : 'Notification Management'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Consulter et gérer les notifications du système'
                : 'View and manage all system notifications'}
            </p>
            <button
              type="button"
              className="dashboard-wt-btn-primary dashboard-wt-btn-large mt-6"
              onClick={() => navigate('/admin/dashboard')}
            >
              <span className="dashboard-wt-btn-icon">←</span>
              {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
            </button>
          </div>
        </section>

        {/* Stats - Bento */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              {[
                {
                  icon: '🔔',
                  label: language === 'fr' ? 'Total' : 'Total',
                  value: statsSummary.total,
                },
                {
                  icon: '📬',
                  label: language === 'fr' ? 'Non lues' : 'Unread',
                  value: statsSummary.unread,
                },
                {
                  icon: '⚠️',
                  label: language === 'fr' ? 'Urgentes' : 'Urgent',
                  value: statsSummary.urgent,
                },
                {
                  icon: '📅',
                  label: language === 'fr' ? "Aujourd'hui" : 'Today',
                  value: statsSummary.today,
                },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="dashboard-wt-stat-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{stat.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-value">{stat.value}</div>
                    <div className="dashboard-wt-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filters & List */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            <div className="transactions-wt-filters dashboard-wt-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title">
                  🔔 {language === 'fr' ? 'Notifications' : 'Notifications'}
                </h2>
              </div>
              <div className="dashboard-wt-card-content">
                <div className="transactions-wt-form-row">
                  <div className="transactions-wt-form-search">
                    <Input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={
                        language === 'fr' ? 'Rechercher titre / message...' : 'Search title / message...'
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="transactions-wt-form-select">
                    <Select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      options={typeOptions}
                    />
                  </div>
                  <div className="transactions-wt-form-select">
                    <Select
                      value={priorityFilter}
                      onChange={(e) => setPriorityFilter(e.target.value)}
                      options={priorityOptions}
                    />
                  </div>
                  <div className="transactions-wt-form-select">
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      options={statusOptions}
                    />
                  </div>
                  <div className="transactions-wt-form-actions">
                    <button
                      type="button"
                      className="dashboard-wt-btn-primary"
                      onClick={handleSearch}
                    >
                      {language === 'fr' ? 'Rechercher' : 'Search'}
                    </button>
                    <button
                      type="button"
                      className="dashboard-wt-btn-secondary"
                      onClick={handleClear}
                    >
                      {language === 'fr' ? 'Effacer' : 'Clear'}
                    </button>
                    {statsSummary.unread > 0 && (
                      <button
                        type="button"
                        className="dashboard-wt-btn-secondary border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                        onClick={handleMarkAllAsRead}
                      >
                        ✓ {language === 'fr' ? 'Tout marquer comme lu' : 'Mark all as read'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {notifications.length === 0 ? (
              <div className="dashboard-wt-card dashboard-wt-empty">
                <div className="dashboard-wt-empty-icon">🔔</div>
                <h3 className="dashboard-wt-empty-title">
                  {language === 'fr' ? 'Aucune notification' : 'No notifications found'}
                </h3>
                <p className="dashboard-wt-empty-desc">
                  {language === 'fr'
                    ? 'Aucune notification ne correspond aux critères'
                    : 'No notifications match the selected filters'}
                </p>
              </div>
            ) : (
              <>
                <div className="dashboard-wt-transactions transactions-wt-list">
                  {notifications.map((notification, index) => {
                    const norm = normalizeNotification(notification);
                    return (
                      <div
                        key={notification.id}
                        className="dashboard-wt-transaction-wrapper dashboard-wt-card"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <NotificationItem
                          notification={norm}
                          onMarkRead={() => handleMarkAsRead(notification.id)}
                          onDelete={() => handleDelete(notification.id)}
                          onClick={(notif) => {
                            if (notif?.transactionId) {
                              navigate(`/admin/transactions/${notif.transactionId}`);
                            } else if (notif?.disputeId) {
                              navigate(`/admin/disputes/${notif.disputeId}`);
                            }
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                {showPagination && (
                  <div className="transactions-wt-pagination dashboard-wt-card">
                    <div className="transactions-wt-pagination-inner">
                      <span className="transactions-wt-pagination-text">
                        {language === 'fr' ? 'Affichage' : 'Showing'}{' '}
                        {(pag.page - 1) * (pag.limit || 20) + 1}{' '}
                        {language === 'fr' ? 'à' : 'to'}{' '}
                        {Math.min(pag.page * (pag.limit || 20), totalCount)} {language === 'fr' ? 'sur' : 'of'}{' '}
                        {totalCount}
                      </span>
                      <div className="transactions-wt-pagination-btns">
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() => setPagination({ ...pagination, page: (pagination?.page ?? 1) - 1 })}
                          disabled={(pagination?.page ?? 1) <= 1}
                        >
                          {language === 'fr' ? 'Précédent' : 'Previous'}
                        </button>
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() => setPagination({ ...pagination, page: (pagination?.page ?? 1) + 1 })}
                          disabled={(pagination?.page ?? 1) * (pagination?.pageSize ?? 20) >= totalCount}
                        >
                          {language === 'fr' ? 'Suivant' : 'Next'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminNotifications;
